/**
 * AdminScoreWidget.jsx - Admin-only widget to score query results
 * 
 * Shows at the top of results for admins to:
 * - Score the overall query result (1-10)
 * - Leave notes about what was good/bad
 * - See the auto-score and grade
 */

import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, TextInput, StyleSheet, Platform, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { theme } from '../constants/theme';
import Constants from 'expo-constants';

const API_BASE = Constants.expoConfig?.extra?.API_BASE || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const AdminScoreWidget = ({ 
  queryId, 
  autoScore = null, 
  autoGrade = null,
  issues = [],
  positives = [],
  onScoreSubmitted 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [score, setScore] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Animation for expand/collapse
  const [expandAnim] = useState(new Animated.Value(0));
  
  useEffect(() => {
    Animated.timing(expandAnim, {
      toValue: isExpanded ? 1 : 0,
      duration: 200,
      useNativeDriver: false
    }).start();
  }, [isExpanded]);
  
  const handleSubmitScore = async () => {
    if (!score || isNaN(parseFloat(score))) {
      setError('Please enter a valid score (1-10)');
      return;
    }
    
    const numScore = parseFloat(score);
    if (numScore < 1 || numScore > 10) {
      setError('Score must be between 1 and 10');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/queryAssessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'score',
          queryId,
          score: numScore,
          notes: notes.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        if (onScoreSubmitted) {
          onScoreSubmitted({ queryId, score: numScore, notes });
        }
      } else {
        setError(data.error || 'Failed to save score');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const getScoreColor = (s) => {
    if (s >= 8) return '#22C55E'; // green
    if (s >= 6) return '#F59E0B'; // yellow
    if (s >= 4) return '#F97316'; // orange
    return '#EF4444'; // red
  };
  
  const getGradeColor = (grade) => {
    if (grade?.startsWith('A')) return '#22C55E';
    if (grade?.startsWith('B')) return '#3B82F6';
    if (grade?.startsWith('C')) return '#F59E0B';
    return '#EF4444';
  };
  
  return (
    <View style={styles.container}>
      {/* Header bar - always visible */}
      <TouchableOpacity 
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
        activeOpacity={0.7}
      >
        <View style={styles.headerLeft}>
          <Ionicons name="analytics" size={16} color="#A855F7" />
          <Text style={styles.headerTitle}>Admin Score</Text>
          {autoScore !== null && (
            <View style={[styles.autoScoreBadge, { backgroundColor: getScoreColor(autoScore) + '20' }]}>
              <Text style={[styles.autoScoreText, { color: getScoreColor(autoScore) }]}>
                Auto: {autoScore}/10
              </Text>
            </View>
          )}
          {autoGrade && (
            <View style={[styles.gradeBadge, { backgroundColor: getGradeColor(autoGrade) + '20' }]}>
              <Text style={[styles.gradeText, { color: getGradeColor(autoGrade) }]}>{autoGrade}</Text>
            </View>
          )}
        </View>
        <Ionicons 
          name={isExpanded ? "chevron-up" : "chevron-down"} 
          size={18} 
          color={theme.colors.textSecondary} 
        />
      </TouchableOpacity>
      
      {/* Expandable content */}
      <Animated.View style={[
        styles.expandedContent,
        {
          maxHeight: expandAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [0, 300]
          }),
          opacity: expandAnim
        }
      ]}>
        {/* Auto-assessment details */}
        {(issues.length > 0 || positives.length > 0) && (
          <View style={styles.assessmentSection}>
            {positives.length > 0 && (
              <View style={styles.assessmentList}>
                {positives.slice(0, 3).map((pos, i) => (
                  <View key={i} style={styles.assessmentItem}>
                    <Ionicons name="checkmark-circle" size={12} color="#22C55E" />
                    <Text style={styles.assessmentText}>{pos}</Text>
                  </View>
                ))}
              </View>
            )}
            {issues.length > 0 && (
              <View style={styles.assessmentList}>
                {issues.slice(0, 3).map((issue, i) => (
                  <View key={i} style={styles.assessmentItem}>
                    <Ionicons name="alert-circle" size={12} color="#F59E0B" />
                    <Text style={styles.assessmentText}>{issue}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}
        
        {/* Score input */}
        {!submitted ? (
          <View style={styles.scoreInputSection}>
            <View style={styles.scoreRow}>
              <Text style={styles.inputLabel}>Your Score:</Text>
              <View style={styles.scoreInputWrapper}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <TouchableOpacity
                    key={n}
                    style={[
                      styles.scoreButton,
                      score === String(n) && styles.scoreButtonActive,
                      { backgroundColor: score === String(n) ? getScoreColor(n) + '30' : 'transparent' }
                    ]}
                    onPress={() => setScore(String(n))}
                  >
                    <Text style={[
                      styles.scoreButtonText,
                      score === String(n) && { color: getScoreColor(n) }
                    ]}>{n}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            <View style={styles.notesRow}>
              <Text style={styles.inputLabel}>Notes:</Text>
              <TextInput
                style={styles.notesInput}
                placeholder="What was good/bad about this result?"
                placeholderTextColor={theme.colors.textTertiary}
                value={notes}
                onChangeText={setNotes}
                multiline
                numberOfLines={2}
              />
            </View>
            
            {error && (
              <Text style={styles.errorText}>{error}</Text>
            )}
            
            <TouchableOpacity
              style={[styles.submitButton, (!score || submitting) && styles.submitButtonDisabled]}
              onPress={handleSubmitScore}
              disabled={!score || submitting}
            >
              <LinearGradient
                colors={['#A855F7', '#7C3AED']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.submitButtonGradient}
              >
                <Text style={styles.submitButtonText}>
                  {submitting ? 'Saving...' : 'Submit Score'}
                </Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.submittedSection}>
            <Ionicons name="checkmark-circle" size={24} color="#22C55E" />
            <Text style={styles.submittedText}>Score saved: {score}/10</Text>
          </View>
        )}
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'rgba(168, 85, 247, 0.05)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(168, 85, 247, 0.2)',
    marginBottom: 12,
    overflow: 'hidden'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  headerTitle: {
    color: '#A855F7',
    fontSize: 13,
    fontWeight: '600'
  },
  autoScoreBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10
  },
  autoScoreText: {
    fontSize: 11,
    fontWeight: '600'
  },
  gradeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6
  },
  gradeText: {
    fontSize: 11,
    fontWeight: '700'
  },
  expandedContent: {
    overflow: 'hidden',
    paddingHorizontal: 12
  },
  assessmentSection: {
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)'
  },
  assessmentList: {
    gap: 4,
    marginTop: 4
  },
  assessmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6
  },
  assessmentText: {
    color: theme.colors.textSecondary,
    fontSize: 12
  },
  scoreInputSection: {
    paddingVertical: 12
  },
  scoreRow: {
    marginBottom: 12
  },
  inputLabel: {
    color: theme.colors.textSecondary,
    fontSize: 12,
    marginBottom: 6
  },
  scoreInputWrapper: {
    flexDirection: 'row',
    gap: 4,
    flexWrap: 'wrap'
  },
  scoreButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },
  scoreButtonActive: {
    borderColor: 'transparent'
  },
  scoreButtonText: {
    color: theme.colors.textSecondary,
    fontSize: 13,
    fontWeight: '600'
  },
  notesRow: {
    marginBottom: 12
  },
  notesInput: {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    padding: 10,
    color: theme.colors.textPrimary,
    fontSize: 13,
    minHeight: 60,
    textAlignVertical: 'top'
  },
  errorText: {
    color: '#EF4444',
    fontSize: 12,
    marginBottom: 8
  },
  submitButton: {
    borderRadius: 8,
    overflow: 'hidden'
  },
  submitButtonDisabled: {
    opacity: 0.5
  },
  submitButtonGradient: {
    paddingVertical: 10,
    alignItems: 'center'
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600'
  },
  submittedSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16
  },
  submittedText: {
    color: '#22C55E',
    fontSize: 14,
    fontWeight: '600'
  }
});

export default AdminScoreWidget;
